/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TChannel } from "@tivocorp/data-domain-lib";

import { DeviceFeatures, DeviceModel } from "../../device";
import type { Session } from "../../entities";
import { PreviewInteractor } from "../../interactor";
import { Log } from "../../logging/Log";
import { DrmType, PlaybackContentType } from "../enums";
import type {
  IMetadata,
  IPlayable,
  IPlaybackURLManager,
  IVpcDrmInfo
} from "../interfaces";
import { TivoPlusUrlResolver, TivoPlusValueProvider } from "../tivoplus";
import { PlaybackUrlUtils } from "../utils/PlaybackUrlUtils";
import { VodPlaybackUtils } from "../utils/VodPlaybackUtils";
import { VideoProviderConfigModel } from "../../videoProvider";

/** Scheme prefix {@link PlaybackURLManager.toHttps} upgrades. */
const HTTP_SCHEME_PATTERN: RegExp = /^http:\/\//i;

/**
 * Resolves the playback URL for a given content type.
 *
 * Post-session flow: when a {@link Session} with a non-empty `playlistUrl`
 * is provided, that URL is returned directly.
 *
 * Pre-session / fallback flow: delegates to a content-type-specific
 * private method (e.g. extracting an HTTPS URL from `channel.tuningUri`
 * for LIVE content).
 *
 * Thumbnail templates follow the same split: the v2 session's `seekUrl` /
 * `socuSeekUrl` is authoritative once the session has answered, and the
 * channel's `thumbnail-*://` entry (or, for VOD and NDVR, the hardcoded
 * template) serves until then.
 */
export class PlaybackURLManager implements IPlaybackURLManager {
  private static readonly TAG: string = "PlaybackURLManager";

  /**
   * Per-frame placeholder every thumbnail template hands to the thumbnail
   * manager. A channel-delivered template names it `{THUMB_IMAGE_ID}`; the
   * app's thumbnail path (ThumbnailGrid, ThumbnailManager,
   * BrowserThumbnailStrategy) substitutes `{FILENAME}`, so the delivered
   * token is normalized to that on the way out.
   */
  private static readonly THUMBNAIL_FRAME_PLACEHOLDER: string = "{FILENAME}";

  private static readonly THUMBNAIL_FRAME_PLACEHOLDER_DELIVERED: string =
    "{THUMB_IMAGE_ID}";

  /**
   * Tuning-URI scheme per playback type. A channel delivers one entry for
   * each, so the scheme is what says which stream an entry addresses.
   * Lower-case, as {@link getTuningUrisByScheme} compares case-insensitively.
   */
  private static readonly TUNING_SCHEME_LIVE: string = "live://";

  private static readonly TUNING_SCHEME_SOCU: string = "socu://";

  private static readonly TUNING_SCHEME_THUMBNAIL_LIVE: string =
    "thumbnail-live://";

  private static readonly TUNING_SCHEME_THUMBNAIL_SOCU: string =
    "thumbnail-socu://";

  // TODO: hardcoded until the service delivers the NDVR thumbnail template.
  private static readonly NDVR_THUMBNAIL_URL_TEMPLATE: string =
    "https://vod-integration.mobitv.com/mm/cmaf/recordings/{CHANNEL}/thumbnails/640_T/start={STARTTIME},end={ENDTIME},{FILENAME}.jpeg?recid={RECID}";

  private static readonly NDVR_RECORDING_CHANNEL: RegExp =
    /\/mm\/(?:cmaf|dash|hls5)\/recordings\/([^/?]+)\//;

  private static readonly NDVR_RECID_PARAM: string = "recid=";

  private static readonly NDVR_RECORDING_WINDOW: RegExp =
    /start=(\d+),end=(\d+)/;

  // TODO: hardcoded until the service delivers the VOD thumbnail template.
  private static readonly VOD_THUMBNAIL_URL_TEMPLATE: string =
    "https://vod-integration.mobitv.com/mm/cmaf/vod/{PROVIDER}/{ASSET}/thumbnails/640_T/{FILENAME}.jpeg";

  private static readonly VOD_ASSET_PATH: RegExp =
    /\/mm\/(?:cmaf|dash)\/vod\/([^/?]+)\/([^/?]+)\//;

  /**
   * Every scheme above — what makes an entry "typed".
   *
   * Kept as an explicit list because "untyped" cannot be derived from the
   * scheme alone: a legacy entry (`ip-abr://…`, `qam://…`) carries no
   * playback-type scheme at all, and callers that opt into the untyped
   * fallback need to tell those apart from an entry addressing a different
   * playback type.
   */
  private static readonly TUNING_SCHEMES: readonly string[] = [
    PlaybackURLManager.TUNING_SCHEME_LIVE,
    PlaybackURLManager.TUNING_SCHEME_SOCU,
    PlaybackURLManager.TUNING_SCHEME_THUMBNAIL_LIVE,
    PlaybackURLManager.TUNING_SCHEME_THUMBNAIL_SOCU
  ];

  /**
   * Placeholder a playback URL carries in place of the operator's switch
   * (transcode) group — e.g. `…/drm=widevine,group={SWITCHGROUP}.mpd`. The
   * group itself is per-operator and arrives on the `playbackConfig` device
   * feature, so only the client can fill it in.
   */
  private static readonly SWITCH_GROUP_PLACEHOLDER: string = "{SWITCHGROUP}";

  /**
   * Placeholders a `socu://` tuning URI carries in place of the airing window
   * — e.g. `…,start={STARTTIME},end={ENDTIME}.mpd`. Both are filled from the
   * SOCU watch action; see {@link getSocuWindowEpochs}.
   */
  private static readonly START_TIME_PLACEHOLDER: string = "{STARTTIME}";

  private static readonly END_TIME_PLACEHOLDER: string = "{ENDTIME}";

  /**
   * Above this, a numeric timestamp is read as milliseconds rather than
   * seconds. Any plausible airing epoch in seconds is far below it, and any
   * in milliseconds far above, so the split is unambiguous for real data.
   */
  private static readonly EPOCH_MILLIS_THRESHOLD: number = 1e12;

  /**
   * Lowest Samsung Tizen OS version able to play the CMAF-packaged stream.
   * Devices below this fall back to the DASH-packaged manifest.
   */
  private static readonly TIZEN_MIN_CMAF_MAJOR: number = 6;

  private static readonly TIZEN_MIN_CMAF_MINOR: number = 5;

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * Resolves the playback URL for the given content, ready to play.
   *
   * Both URL sources — the session's authoritative `playlistUrl` and the
   * pre-session `tuningUri` — pass through the same TiVo+ resolution step on
   * the way out, because either can arrive as a template and the two must
   * resolve identically or a fast tune would see a URL change and reload.
   */
  public getPlaybackUrl(
    playable: IPlayable,
    metadata: IMetadata,
    drmConfig: IVpcDrmInfo,
    session?: Session
  ): string {
    let url = this.selectPlaybackUrl(playable, metadata, drmConfig, session);
    url = this.getModifiedUrl(url);
    url = PlaybackURLManager.applySwitchGroupResolution(url);
    url = this.applyTivoPlusResolution(playable, url, metadata);

    // Logged after every transform has run, so this is the exact string handed
    // to the player — the one worth having when a stream fails to load.
    Log.i(
      PlaybackURLManager.TAG,
      "getPlaybackUrl() - contentType=" +
        String(playable.playbackContentType) +
        " url=" +
        (url === "" ? "<none>" : url)
    );

    return url;
  }

  // ── Private — URL selection ──────────────────────────────────────────────

  /** Picks the URL to play, before any ad-parameter resolution. */
  private selectPlaybackUrl(
    playable: IPlayable,
    metadata: IMetadata,
    drmConfig: IVpcDrmInfo,
    session?: Session
  ): string {
    Log.i(PlaybackURLManager.TAG, "selectPlaybackUrl()");

    // Post-session flow — session URL takes priority
    if (
      session !== undefined &&
      session !== null &&
      session.playlistUrl !== undefined &&
      session.playlistUrl !== null &&
      session.playlistUrl !== ""
    ) {
      Log.d(
        PlaybackURLManager.TAG,
        "selectPlaybackUrl() - resolved from session"
      );
      return session.playlistUrl;
    }

    // A session-less VOD preview has no session URL coming, so it is resolved
    // ahead of the fast-tune gate below: that gate defers to the session, and
    // for this content there is nothing to defer to.
    if (VodPlaybackUtils.isSessionlessPreview(playable, metadata)) {
      return PlaybackURLManager.getVodPreviewAssetUrl(metadata);
    }

    // Pre-session / fallback flow — this is the fast tune, and a provider can
    // switch it off. Gated here rather than per content type because the rule
    // is about the flow, not the content: every branch below resolves a URL to
    // play *before* the session answers, which is exactly what
    // `disableFastTune` forbids. The post-session return above is untouched —
    // disabling the fast tune delays the first frame until the session URL
    // arrives, it does not stop playback.
    if (PlaybackURLManager.isFastTuneDisabled(playable, metadata, drmConfig)) {
      Log.i(
        PlaybackURLManager.TAG,
        "selectPlaybackUrl() - fast tune disabled for this provider; " +
          "deferring to the session URL"
      );
      return "";
    }

    const contentType = playable.playbackContentType;

    switch (contentType) {
      case PlaybackContentType.LIVE:
        return this.getLinearPlaybackUrl(playable, drmConfig);

      case PlaybackContentType.NDVR_LIVE:
      case PlaybackContentType.NDVR_RECORDED:
        return this.getNdvrPlaybackUrl(playable, metadata);

      case PlaybackContentType.STARTOVER_BEGINNING:
      case PlaybackContentType.STARTOVER_RESUME:
      case PlaybackContentType.CATCHUP_BEGINNING:
      case PlaybackContentType.CATCHUP_RESUME:
        return this.getSocuPlaybackUrl(playable, metadata, drmConfig);

      case PlaybackContentType.VOD:
        return this.getVodPlaybackUrl(playable, metadata);

      default:
        Log.e(
          PlaybackURLManager.TAG,
          "selectPlaybackUrl() - unknown content type: " + String(contentType)
        );
        return "";
    }
  }

  /**
   * Whether the provider serving this content has fast tune switched off.
   *
   * The partner id is read off the content first — `linearProviderPartnerId`
   * for anything with a channel; for VOD the watch action's `partnerId`, then
   * the playable's `videoProviderId` — because that is the provider of the
   * content about to be resolved. `drmConfig` is only a
   * fallback: it describes whatever DRM is currently configured, which on a
   * channel change can still be the previous tune's provider, and it is the
   * one source left when a playable carries neither a channel nor a VOD model.
   *
   * An unresolvable partner id leaves fast tune enabled, matching
   * {@link VideoProviderConfigModel.isFastTuneDisabled}: the behaviour only
   * changes where a provider positively asked for it.
   */
  private static isFastTuneDisabled(
    playable: IPlayable,
    metadata: IMetadata,
    drmConfig: IVpcDrmInfo
  ): boolean {
    return VideoProviderConfigModel.isFastTuneDisabled(
      PlaybackURLManager.getProviderPartnerId(playable, metadata, drmConfig)
    );
  }

  /** The provider partner id for this content, or `""` when none is known. */
  private static getProviderPartnerId(
    playable: IPlayable,
    metadata: IMetadata,
    drmConfig: IVpcDrmInfo
  ): string {
    const channel = playable.channel;

    if (channel !== undefined && channel !== null) {
      const linearId = channel.linearProviderPartnerId;
      if (linearId !== undefined && linearId !== null && linearId !== "") {
        return linearId;
      }
    }

    const vodProviderId = VodPlaybackUtils.getProviderId(playable, metadata);

    if (vodProviderId !== "") {
      return vodProviderId;
    }

    const drmId = drmConfig.providerPartnerId;

    return drmId !== undefined && drmId !== null ? drmId : "";
  }

  /**
   * Substitutes the `TIVOPLUS_*` ad-parameter placeholders a TiVo+ playback
   * URL arrives with.
   *
   * Applied whenever the URL actually carries a placeholder rather than when
   * the channel classifies as TiVo+. The two agree in practice, but keying on
   * the URL means a classification gap — a provider config that has not
   * loaded, or a partner id that is missing from it — still yields a playable,
   * correctly targeted URL instead of one that reaches the content partner
   * with literal `TIVOPLUS_` tokens in it. It is also what keeps this a no-op
   * for every other channel: a URL with no placeholder is returned unchanged,
   * so the existing linear flow is untouched.
   */
  private applyTivoPlusResolution(
    playable: IPlayable,
    url: string,
    metadata: IMetadata
  ): string {
    if (url === "" || url.indexOf("TIVOPLUS_") === -1) {
      return url;
    }

    const channel = playable.channel;
    const channelName =
      channel !== undefined && channel !== null
        ? channel.name || channel.callSign || ""
        : "";

    return TivoPlusUrlResolver.resolve(
      url,
      TivoPlusValueProvider.getInstance().getValues(
        playable.playbackContentType
      ),
      {
        channelName: channelName,
        contentGenre: PlaybackURLManager.getContentGenre(metadata),
        contentRating: PlaybackURLManager.getContentRating(metadata)
      }
    );
  }

  /**
   * Substitutes the `{SWITCHGROUP}` placeholder a playback URL arrives with.
   *
   * Applied to every URL on the way out, whichever source it came from: the
   * live and SOCU tuning URIs both carry the placeholder, and a session's
   * `playlistUrl` can too, so keying on the URL rather than on the content
   * type means one step covers all of them and is a no-op — the URL returned
   * unchanged — for any URL without it.
   *
   * The group comes from the `playbackConfig` device feature. When it is
   * missing the URL is left templated rather than patched with a guess: no
   * client-side default exists, an empty `group=` is as unplayable as the
   * literal token, and leaving the token in place keeps the cause legible in
   * the playback error that follows.
   */
  private static applySwitchGroupResolution(url: string): string {
    if (
      url === "" ||
      url.indexOf(PlaybackURLManager.SWITCH_GROUP_PLACEHOLDER) === -1
    ) {
      return url;
    }

    const switchGroup: string = DeviceFeatures.getSwitchGroup();

    if (switchGroup === "") {
      Log.e(
        PlaybackURLManager.TAG,
        "applySwitchGroupResolution() - no switch group on the playbackConfig " +
          "feature; URL left templated"
      );
      return url;
    }

    Log.d(
      PlaybackURLManager.TAG,
      "applySwitchGroupResolution() - resolved to " + switchGroup
    );

    return url.replaceAll(
      PlaybackURLManager.SWITCH_GROUP_PLACEHOLDER,
      switchGroup
    );
  }

  /** Genre/category label(s) for the current program, verbatim from the preview API. */
  private static getContentGenre(metadata: IMetadata): string {
    const preview = metadata.offerPreview;
    if (preview === undefined || preview === null) {
      return "";
    }
    const categoryLabel = preview.categoryLabel;
    return typeof categoryLabel === "string" ? categoryLabel : "";
  }

  /**
   * Resolved parental-rating display name for the current program.
   *
   * The specification requires a single value: when the offer carries
   * multiple ratings, only the first resolved name is sent, not the whole
   * list.
   */
  private static getContentRating(metadata: IMetadata): string {
    const preview = metadata.offerPreview;
    if (preview === undefined || preview === null) {
      return "";
    }
    const ratingList = preview.internalRatingList;
    if (ratingList === undefined || ratingList === null) {
      return "";
    }

    for (const rating of ratingList) {
      const typeId = rating.typeId !== undefined ? rating.typeId : "";
      const valueId = rating.valueId !== undefined ? rating.valueId : "";
      const ratingName = PreviewInteractor.fetchInternalRatingName(
        typeId + valueId
      );
      if (ratingName !== "") {
        return ratingName;
      }
    }

    return "";
  }

  // ── Private — content-type-specific resolvers ────────────────────────────

  /**
   * Extracts the first HTTP(S) URL embedded inside `channel.tuningUri`.
   *
   * Tuning URIs use a custom scheme:
   * `ip-abr://dashTransportStream/.../https://example.com/stream.mpd`
   *
   * Selection runs in two steps. The entries are first narrowed to the ones
   * carrying the `live://` scheme — see {@link getTuningUrisByScheme}, which
   * keeps a channel's `socu://` and `thumbnail-*` entries out of the linear
   * path — and the DRM/transport filter then chooses among those.
   *
   * The scan finds the first `http(s)://` run anywhere in the string, so no
   * assumption is made about how many segments precede it or what they hold
   * — this works for any provider's tuning-URI format.
   *
   * TiVo+ templates are the one case that needs help: they embed the URL as
   * `https:TIVOPLUS_BLANK//host/…`, with the placeholder standing in for the
   * `//` so the raw template holds no literal `://` and the scan would find
   * nothing. Resolving that structural placeholder first restores a scannable
   * URL, and is a no-op for every other channel — so one path serves all of
   * them. Ad-parameter substitution happens later, on the URL this returns.
   */
  private getLinearPlaybackUrl(
    playable: IPlayable,
    drmConfig: IVpcDrmInfo
  ): string {
    const channel = playable.channel;

    if (channel === undefined || channel === null) {
      Log.e(
        PlaybackURLManager.TAG,
        "getLinearPlaybackUrl() - channel is missing"
      );
      return "";
    }

    const tuningUri = channel.tuningUri;

    if (
      tuningUri === undefined ||
      tuningUri === null ||
      tuningUri.length === 0
    ) {
      Log.e(
        PlaybackURLManager.TAG,
        "getLinearPlaybackUrl() - tuningUri is empty"
      );
      return "";
    }

    const liveTuningUris: string[] = PlaybackURLManager.getTuningUrisByScheme(
      tuningUri,
      PlaybackURLManager.TUNING_SCHEME_LIVE,
      true
    );

    if (liveTuningUris.length === 0) {
      Log.e(
        PlaybackURLManager.TAG,
        "getLinearPlaybackUrl() - tuningUri carries no live entry"
      );
      return "";
    }

    const embedded: string = PlaybackURLManager.selectUriMatchingDrm(
      liveTuningUris,
      drmConfig
    );

    if (embedded === "") {
      Log.e(
        PlaybackURLManager.TAG,
        "getLinearPlaybackUrl() - no HTTPS URL found in tuningUri"
      );
    }

    return embedded;
  }

  /**
   * Picks the first entry whose DRM and transport segments the client can
   * play, and returns the URL embedded in it.
   *
   * Shared by the linear and SOCU paths, which face the same choice once the
   * entries have been narrowed by scheme: a channel delivers one entry per
   * DRM/transport combination, and only the segments say which of them this
   * client can decrypt. Clear playback is the one case matched loosely — a
   * `*` in place of a concrete DRM segment means "no DRM", which a Clear
   * client can always take.
   *
   * @param uris - Entries already narrowed to one playback type
   * @param drmConfig - The DRM and transport this client negotiated
   * @returns The embedded URL of the first playable entry, or `""` when none
   * matches or no match carries a URL
   */
  private static selectUriMatchingDrm(
    uris: string[],
    drmConfig: IVpcDrmInfo
  ): string {
    const drmType = drmConfig.drmType.toString();
    const transportEncodingType = drmConfig.transportEncodingType;

    for (const uri of uris) {
      const splitURI = uri.split("/");

      if (
        (splitURI.includes(drmType) &&
          splitURI.includes(transportEncodingType!)) ||
        (drmType === DrmType.Clear && splitURI.includes("*"))
      ) {
        const embedded = PlaybackURLManager.extractEmbeddedUrl(uri);
        if (embedded !== "") {
          return embedded;
        }
      }
    }

    return "";
  }

  /**
   * Extracts the HTTP(S) URL embedded in one tuning URI.
   *
   * The scan finds the first `http(s)://` run anywhere in the string, so no
   * assumption is made about how many segments precede it or what they hold
   * — this works for any provider's tuning-URI format.
   *
   * TiVo+ templates are the one case that needs help: they embed the URL as
   * `https:TIVOPLUS_BLANK//host/…`, with the placeholder standing in for the
   * `//` so the raw template holds no literal `://` and the scan would find
   * nothing. Resolving that structural placeholder first restores a scannable
   * URL, and is a no-op for every other channel — so one path serves all of
   * them. Ad-parameter substitution happens later, on the URL this returns.
   *
   * @param uri - One `tuningUri` entry
   * @returns The embedded URL, or `""` when the entry carries none
   */
  private static extractEmbeddedUrl(uri: string): string {
    const scannable = TivoPlusUrlResolver.resolveStructuralPlaceholders(uri);
    const match = scannable.match(/\bhttps?:\/\/\S+/gi);

    return match !== null && match.length > 0 ? match[0] : "";
  }

  /**
   * Selects the `tuningUri` entries addressing one playback type.
   *
   * A channel delivers one entry per type, and they all share the same
   * transport and DRM segments:
   *
   * ```
   * live://dashFmp4TransportStream/mobiWidevine/https://…/live/…mpd
   * socu://dashFmp4TransportStream/mobiWidevine/https://…/socu/…mpd
   * thumbnail-live://dashFmp4TransportStream/mobiWidevine/https://…jpeg
   * ```
   *
   * So a DRM/transport filter cannot tell them apart — every one of them
   * matches — and on its own returns whichever comes first in the array. That
   * would make playback depend on the order the backend happens to send, and
   * a reordered response would hand the player a manifest for the wrong
   * playback type, or a thumbnail JPEG template. Narrowing by scheme first is
   * what makes the choice deliberate.
   *
   * @param tuningUri - Every tuning URI delivered for the channel
   * @param scheme - One of the `TUNING_SCHEME_*` constants
   * @param includeUntyped - When true and no entry carries `scheme`, fall back
   * to the entries with no playback-type scheme at all (`ip-abr://…`,
   * `qam://…`). Only the live path wants this: a legacy channel predating the
   * per-type schemes delivers its linear stream that way, and the fallback is
   * what keeps it filtering exactly as it did before. A legacy entry is never
   * a SOCU manifest or a thumbnail template, so those callers leave it off.
   * @returns The matching entries, in delivery order; the untyped entries when
   * none match and `includeUntyped` is set; otherwise an empty array
   */
  private static getTuningUrisByScheme(
    tuningUri: string[],
    scheme: string,
    includeUntyped: boolean = false
  ): string[] {
    const matchingUris: string[] = [];
    const untypedUris: string[] = [];

    for (const uri of tuningUri) {
      const normalized = uri.trim().toLowerCase();

      if (normalized.startsWith(scheme)) {
        matchingUris.push(uri);
        continue;
      }

      if (includeUntyped && !PlaybackURLManager.hasTuningScheme(normalized)) {
        untypedUris.push(uri);
      }
    }

    return matchingUris.length > 0 ? matchingUris : untypedUris;
  }

  /** True when the entry carries any known playback-type scheme. */
  private static hasTuningScheme(normalizedUri: string): boolean {
    return PlaybackURLManager.TUNING_SCHEMES.some((scheme: string) =>
      normalizedUri.startsWith(scheme)
    );
  }

  /**
   * Resolves a playback URL for NDVR content.
   *
   * Currently NDVR URLs are only available from the session API response.
   * This method serves as a fallback extension point; it extracts a
   * `playUri` from the watch-action kernel when present.
   */
  private getNdvrPlaybackUrl(
    _playable: IPlayable,
    metadata: IMetadata
  ): string {
    const playUri = this.extractPlayUriFromKernel(metadata.watchAction);

    if (playUri !== "") {
      return playUri;
    }

    Log.d(
      PlaybackURLManager.TAG,
      "getNdvrPlaybackUrl() - no pre-session URL available"
    );
    return "";
  }

  /**
   * Resolves the pre-session playback URL for SOCU (Start Over / Catch Up)
   * content, from the channel's own `socu://` tuning entry.
   *
   * The entry is delivered templated on the airing window:
   *
   * ```
   * socu://dashFmp4TransportStream/mobiWidevine/https://…/socu/<channel>/
   *   drm=widevine,group={SWITCHGROUP},start={STARTTIME},end={ENDTIME}.mpd
   * ```
   *
   * Only the window is filled here; `{SWITCHGROUP}` is resolved on the way out
   * of {@link getPlaybackUrl}, which every URL passes through.
   *
   * This is what lets SOCU start playing without waiting for the session — the
   * session's `playlistUrl` is still authoritative and replaces this URL when
   * the two differ. So every miss below returns `""` rather than an error: an
   * absent pre-session URL simply means playback starts when the session
   * responds, which is what it did before this path existed.
   */
  private getSocuPlaybackUrl(
    playable: IPlayable,
    metadata: IMetadata,
    drmConfig: IVpcDrmInfo
  ): string {
    const channel = playable.channel;

    if (channel === undefined || channel === null) {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuPlaybackUrl() - channel is missing"
      );
      return "";
    }

    const tuningUri = channel.tuningUri;

    if (
      tuningUri === undefined ||
      tuningUri === null ||
      tuningUri.length === 0
    ) {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuPlaybackUrl() - tuningUri is empty"
      );
      return "";
    }

    const socuTuningUris: string[] = PlaybackURLManager.getTuningUrisByScheme(
      tuningUri,
      PlaybackURLManager.TUNING_SCHEME_SOCU
    );

    if (socuTuningUris.length === 0) {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuPlaybackUrl() - tuningUri carries no socu entry"
      );
      return "";
    }

    const url: string = PlaybackURLManager.selectUriMatchingDrm(
      socuTuningUris,
      drmConfig
    );

    if (url === "") {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuPlaybackUrl() - no HTTPS URL found in the socu entry"
      );
      return "";
    }

    return PlaybackURLManager.applyAiringWindowResolution(url, metadata);
  }

  /**
   * Substitutes the `{STARTTIME}` and `{ENDTIME}` placeholders a `socu://`
   * playback URL arrives with.
   *
   * A URL carrying neither is returned unchanged — an operator delivering a
   * concrete window needs no help. One that carries either and has no window
   * to fill it with yields `""` instead: unlike `{SWITCHGROUP}`, where the
   * tuning URI is the only source and leaving the token in place keeps the
   * cause legible in the playback error, here the session URL is still on its
   * way, so dropping the pre-session URL costs nothing and a templated URL
   * would turn a clean wait into a playback failure.
   */
  private static applyAiringWindowResolution(
    url: string,
    metadata: IMetadata
  ): string {
    const hasStart: boolean =
      url.indexOf(PlaybackURLManager.START_TIME_PLACEHOLDER) !== -1;
    const hasEnd: boolean =
      url.indexOf(PlaybackURLManager.END_TIME_PLACEHOLDER) !== -1;

    if (!hasStart && !hasEnd) {
      return url;
    }

    const window = PlaybackURLManager.getSocuWindowEpochs(metadata);

    if (window === null) {
      Log.e(
        PlaybackURLManager.TAG,
        "applyAiringWindowResolution() - watch action carries no airing " +
          "window; no pre-session URL"
      );
      return "";
    }

    Log.d(
      PlaybackURLManager.TAG,
      "applyAiringWindowResolution() - start=" +
        String(window.start) +
        " end=" +
        String(window.end)
    );

    return url
      .replaceAll(
        PlaybackURLManager.START_TIME_PLACEHOLDER,
        String(window.start)
      )
      .replaceAll(PlaybackURLManager.END_TIME_PLACEHOLDER, String(window.end));
  }

  /**
   * Reads the airing window off the SOCU watch action, as epoch seconds.
   *
   * The action states the start and how long the airing runs, so the end is
   * derived rather than delivered. Both fields are read from the watch action
   * alone: the preview and airing-offer metadata describe the same programme
   * but come from a different service, and a window assembled from the two
   * could address a stream segment the recording does not hold.
   *
   * `startTime` is accepted both at the top level and on the kernel, and in
   * either encoding a backend might use — an ISO-8601 string, or a number in
   * seconds or milliseconds — because this is the one value the URL cannot be
   * built without, and normalizing here is cheaper than a playback failure.
   *
   * @returns The window, or `null` when either end is unavailable or the
   * duration is not positive
   */
  private static getSocuWindowEpochs(
    metadata: IMetadata
  ): { start: number; end: number } | null {
    const watchAction = metadata.watchAction;

    if (watchAction === undefined || watchAction === null) {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuWindowEpochs() - no watch action on the metadata"
      );
      return null;
    }

    const kernel: Record<string, unknown> =
      "kernel" in watchAction &&
      watchAction.kernel !== undefined &&
      watchAction.kernel !== null
        ? (watchAction.kernel as Record<string, unknown>)
        : {};

    const start: number = PlaybackURLManager.toEpochSeconds(
      watchAction.startTime !== undefined
        ? watchAction.startTime
        : kernel.startTime
    );

    if (start === 0) {
      // Names what was actually delivered, so a watch action that carries the
      // window under different keys than expected is identifiable from one log
      // line rather than a debugging session.
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuWindowEpochs() - unusable startTime: watchAction.startTime=" +
          JSON.stringify(watchAction.startTime) +
          " kernel.startTime=" +
          JSON.stringify(kernel.startTime) +
          " watchAction keys=[" +
          Object.keys(watchAction).join(",") +
          "] kernel keys=[" +
          Object.keys(kernel).join(",") +
          "]"
      );
      return null;
    }

    const duration: unknown =
      kernel.duration !== undefined ? kernel.duration : watchAction.duration;

    const durationSeconds: number =
      typeof duration === "number" && Number.isFinite(duration)
        ? Math.floor(duration)
        : 0;

    if (durationSeconds <= 0) {
      Log.e(
        PlaybackURLManager.TAG,
        "getSocuWindowEpochs() - unusable duration: kernel.duration=" +
          JSON.stringify(kernel.duration) +
          " watchAction.duration=" +
          JSON.stringify(watchAction.duration) +
          " kernel keys=[" +
          Object.keys(kernel).join(",") +
          "]"
      );
      return null;
    }

    return { start: start, end: start + durationSeconds };
  }

  /**
   * Normalizes a delivered timestamp to epoch seconds.
   *
   * @param value - An ISO-8601 string, or a number in seconds or milliseconds
   * @returns The epoch in seconds, or `0` when the value is absent or
   * unparseable
   */
  private static toEpochSeconds(value: unknown): number {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      return value > PlaybackURLManager.EPOCH_MILLIS_THRESHOLD
        ? Math.floor(value / 1000)
        : Math.floor(value);
    }

    if (typeof value === "string" && value !== "") {
      const ms: number = Date.parse(value);
      return Number.isNaN(ms) || ms <= 0 ? 0 : Math.floor(ms / 1000);
    }

    return 0;
  }

  /**
   * Resolves the SOCU/Start Over thumbnail URL template for the given channel.
   *
   * The template comes from the channel's own `thumbnail-socu://` tuning entry,
   * exactly as the live path reads `thumbnail-live://`. The operator owns the
   * recording host, path and frame size, so the channel is the only source —
   * it also keeps thumbnails on the same host the `socu://` playback URL
   * resolves to, which a client-side host cannot guarantee.
   *
   * Once the session has answered, its `socuSeekUrl` takes over when it
   * addresses different frames — see {@link resolveThumbnailTemplate}.
   *
   * @param channel - The channel being played, read for its tuning URIs.
   * @param session - The session for the current content, once it has
   * answered; omitted pre-session.
   * @returns A thumbnail URL template with a `{FILENAME}` placeholder, or `""`
   * when neither the session nor the channel delivers a usable template.
   */
  public getSocuThumbnailUrlTemplate(
    channel?: TChannel,
    session?: Session
  ): string {
    return PlaybackURLManager.resolveThumbnailTemplate(
      channel,
      PlaybackURLManager.TUNING_SCHEME_THUMBNAIL_SOCU,
      session !== undefined && session !== null
        ? session.socuSeekUrl
        : undefined,
      "getSocuThumbnailUrlTemplate()"
    );
  }

  /**
   * Resolves the LIVE thumbnail URL template for the given channel.
   *
   * The template comes from the channel's own `thumbnail-live://` tuning
   * entry. The operator owns the thumbnail host, path and frame size, so
   * reading them off the channel is what keeps this correct per-operator.
   * The delivered `{THUMB_IMAGE_ID}` token is normalized to `{FILENAME}`,
   * which is what the thumbnail path substitutes per-frame.
   *
   * Once the session has answered, its `seekUrl` takes over when it addresses
   * different frames — see {@link resolveThumbnailTemplate}.
   *
   * @param channel - The channel being played, read for its tuning URIs.
   * @param session - The session for the current content, once it has
   * answered; omitted pre-session.
   * @returns A thumbnail URL template with a `{FILENAME}` placeholder, or `""`
   * when neither the session nor the channel delivers a usable template.
   */
  public getLiveThumbnailUrlTemplate(
    channel?: TChannel,
    session?: Session
  ): string {
    return PlaybackURLManager.resolveThumbnailTemplate(
      channel,
      PlaybackURLManager.TUNING_SCHEME_THUMBNAIL_LIVE,
      session !== undefined && session !== null ? session.seekUrl : undefined,
      "getLiveThumbnailUrlTemplate()"
    );
  }

  /**
   * Resolves the NDVR recording thumbnail URL template: `{CHANNEL}` and
   * `{RECID}` come from the recording's playback URL, `{STARTTIME}` and
   * `{ENDTIME}` from the caller. `{FILENAME}` is left for the
   * thumbnail manager to fill per frame.
   *
   * The session's `seekUrl`, when present, is used instead: NDVR is
   * session-only content, so there is no pre-session template to compare it
   * against, and the hardcoded template is only the stand-in for a session
   * that does not deliver one.
   *
   * @param playbackUrl - The NDVR playback (manifest) URL currently loaded.
   * @param startEpoch - Recording start, epoch seconds.
   * @param endEpoch - Recording end, epoch seconds.
   * @param session - The session for the current content, once it has
   * answered.
   * @returns The template, or `""` when the session carries no `seekUrl` and
   * the playback URL carries no recording channel or recid.
   */
  public getNdvrThumbnailUrlTemplate(
    playbackUrl: string | null,
    startEpoch: number,
    endEpoch: number,
    session?: Session
  ): string {
    const sessionTemplate: string =
      PlaybackURLManager.getSessionSeekTemplate(session);

    if (sessionTemplate !== "") {
      const template: string = sessionTemplate
        .replaceAll(
          PlaybackURLManager.START_TIME_PLACEHOLDER,
          String(startEpoch)
        )
        .replaceAll(PlaybackURLManager.END_TIME_PLACEHOLDER, String(endEpoch));

      Log.d(
        PlaybackURLManager.TAG,
        "getNdvrThumbnailUrlTemplate() - resolved from session: " + template
      );

      return template;
    }

    const channel: string = PlaybackURLManager.getNdvrChannel(playbackUrl);
    const recid: string = PlaybackURLManager.getNdvrRecid(playbackUrl);

    if (channel === "" || recid === "") {
      Log.d(
        PlaybackURLManager.TAG,
        "getNdvrThumbnailUrlTemplate() - playback URL carries no recording channel/recid"
      );
      return "";
    }

    const template: string =
      PlaybackURLManager.NDVR_THUMBNAIL_URL_TEMPLATE.replace(
        "{CHANNEL}",
        channel
      )
        .replace("{RECID}", recid)
        .replace("{STARTTIME}", String(startEpoch))
        .replace("{ENDTIME}", String(endEpoch));

    Log.d(
      PlaybackURLManager.TAG,
      "getNdvrThumbnailUrlTemplate() - resolved: " + template
    );

    return template;
  }

  /**
   * Resolves the VOD thumbnail URL template: `{PROVIDER}` and `{ASSET}` come
   * from the title's playback URL. `{FILENAME}` is left for the thumbnail
   * manager to fill per frame.
   *
   * The session's `seekUrl`, when present, is used instead — see
   * {@link getNdvrThumbnailUrlTemplate} for why no comparison is needed.
   *
   * @param playbackUrl - The VOD playback (manifest) URL currently loaded.
   * @param session - The session for the current content, once it has
   * answered.
   * @returns The template, or `""` when the session carries no `seekUrl` and
   * the playback URL carries no VOD provider/asset.
   */
  public getVodThumbnailUrlTemplate(
    playbackUrl: string | null,
    session?: Session
  ): string {
    const sessionTemplate: string =
      PlaybackURLManager.getSessionSeekTemplate(session);

    if (sessionTemplate !== "") {
      Log.d(
        PlaybackURLManager.TAG,
        "getVodThumbnailUrlTemplate() - resolved from session: " +
          sessionTemplate
      );
      return sessionTemplate;
    }

    const match: RegExpExecArray | null =
      playbackUrl !== null
        ? PlaybackURLManager.VOD_ASSET_PATH.exec(playbackUrl)
        : null;

    if (match === null) {
      Log.d(
        PlaybackURLManager.TAG,
        "getVodThumbnailUrlTemplate() - playback URL carries no VOD provider/asset"
      );
      return "";
    }

    const template: string =
      PlaybackURLManager.VOD_THUMBNAIL_URL_TEMPLATE.replace(
        "{PROVIDER}",
        match[1]
      ).replace("{ASSET}", match[2]);

    Log.d(
      PlaybackURLManager.TAG,
      "getVodThumbnailUrlTemplate() - resolved: " + template
    );

    return template;
  }

  /**
   * Reads the recorded window off the NDVR playback URL. A partial recording
   * covers less than its airing, and the player timeline starts here.
   *
   * @param playbackUrl - The NDVR playback (manifest) URL currently loaded.
   * @returns The window in epoch seconds, or zeros when the URL carries none.
   */
  public getNdvrRecordingWindow(playbackUrl: string | null): {
    startEpoch: number;
    endEpoch: number;
  } {
    const path: string = playbackUrl !== null ? playbackUrl.split("?")[0] : "";
    const match: RegExpExecArray | null =
      PlaybackURLManager.NDVR_RECORDING_WINDOW.exec(path);

    return match !== null
      ? { startEpoch: Number(match[1]), endEpoch: Number(match[2]) }
      : { startEpoch: 0, endEpoch: 0 };
  }

  private static getNdvrChannel(playbackUrl: string | null): string {
    const match: RegExpExecArray | null =
      playbackUrl !== null
        ? PlaybackURLManager.NDVR_RECORDING_CHANNEL.exec(playbackUrl)
        : null;

    return match !== null ? match[1] : "";
  }

  /** Returns the recid query value as delivered (already URL-encoded). */
  private static getNdvrRecid(playbackUrl: string | null): string {
    const queryIndex: number =
      playbackUrl !== null ? playbackUrl.indexOf("?") : -1;

    if (playbackUrl === null || queryIndex < 0) {
      return "";
    }

    const recidParam: string | undefined = playbackUrl
      .slice(queryIndex + 1)
      .split("&")
      .find((param: string): boolean =>
        param.startsWith(PlaybackURLManager.NDVR_RECID_PARAM)
      );

    return recidParam !== undefined
      ? recidParam.slice(PlaybackURLManager.NDVR_RECID_PARAM.length)
      : "";
  }

  /**
   * Shared body of the live and SOCU thumbnail resolvers: only the scheme,
   * the session field and the log prefix differ.
   *
   * Mirrors how the playback URL is chosen. The channel's template is the
   * pre-session fast path; once the session answers, its template is
   * authoritative and replaces the channel's only when the two address
   * different frames. When they are equivalent the channel string is returned
   * unchanged, so the caller's thumbnail source key does not move and nothing
   * reloads — the same reasoning as the `playlistUrl` swap in
   * `VideoPlaybackController.onSessionSuccess()`.
   *
   * There is no client-side fallback. The thumbnail host is the operator's to
   * name, and a channel that does not name one has no thumbnails this client
   * can address — guessing a host produces 404s per frame rather than a clean
   * absence, and hides the gap from whoever provisions the lineup. The caller
   * reads `""` as "no thumbnails for this content" and skips the load, which
   * leaves trick play working without the filmstrip.
   *
   * DRM and transport are not filtered on, unlike the playback path: these
   * frames are plain JPEGs, so the DRM segment an entry carries says nothing
   * about whether the client can read them. The first entry with an embedded
   * URL wins.
   */
  private static resolveThumbnailTemplate(
    channel: TChannel | undefined,
    scheme: string,
    sessionSeekUrl: string | undefined,
    logPrefix: string
  ): string {
    const template: string = PlaybackURLManager.getThumbnailTemplateFromChannel(
      channel,
      scheme
    );
    const sessionTemplate: string =
      typeof sessionSeekUrl === "string" && sessionSeekUrl !== ""
        ? PlaybackURLManager.normalizeThumbnailTemplate(sessionSeekUrl)
        : "";

    if (
      sessionTemplate !== "" &&
      !PlaybackUrlUtils.areEquivalent(sessionTemplate, template)
    ) {
      Log.d(
        PlaybackURLManager.TAG,
        logPrefix + " - resolved from session: " + sessionTemplate
      );
      return sessionTemplate;
    }

    if (template === "") {
      Log.e(
        PlaybackURLManager.TAG,
        logPrefix +
          " - channel carries no " +
          scheme.replace("://", "") +
          " entry; no thumbnails"
      );
      return "";
    }

    Log.d(
      PlaybackURLManager.TAG,
      logPrefix + " - resolved from channel tuningUri: " + template
    );

    return template;
  }

  /**
   * Reads a thumbnail URL template off the channel's tuning URIs.
   *
   * Shared by the live and SOCU thumbnail paths: only the scheme differs.
   * Any `{SWITCHGROUP}` in the delivered template is resolved here too, so a
   * thumbnail URL cannot reach the network still templated — the playback
   * path gets that step in `getPlaybackUrl()`, which thumbnails never pass
   * through.
   *
   * @param channel - The channel being played, if known
   * @param scheme - `TUNING_SCHEME_THUMBNAIL_LIVE` or `_SOCU`
   * @returns The template with a `{FILENAME}` placeholder, or `""` when the
   * channel delivers no usable entry for that scheme
   */
  private static getThumbnailTemplateFromChannel(
    channel: TChannel | undefined,
    scheme: string
  ): string {
    if (channel === undefined || channel === null) {
      return "";
    }

    const tuningUri = channel.tuningUri;

    if (
      tuningUri === undefined ||
      tuningUri === null ||
      tuningUri.length === 0
    ) {
      return "";
    }

    const thumbnailUris: string[] = PlaybackURLManager.getTuningUrisByScheme(
      tuningUri,
      scheme
    );

    for (const uri of thumbnailUris) {
      const embedded = PlaybackURLManager.extractEmbeddedUrl(uri);

      if (embedded !== "") {
        return PlaybackURLManager.normalizeThumbnailTemplate(embedded);
      }
    }

    return "";
  }

  /**
   * The session's `seekUrl`, normalized, or `""` when the session has not
   * answered or carries none (a v1 session never does).
   */
  private static getSessionSeekTemplate(session: Session | undefined): string {
    const seekUrl: string | undefined =
      session !== undefined && session !== null ? session.seekUrl : undefined;

    return typeof seekUrl === "string" && seekUrl !== ""
      ? PlaybackURLManager.normalizeThumbnailTemplate(seekUrl)
      : "";
  }

  /**
   * Brings a delivered thumbnail template into the form the thumbnail path
   * expects, whichever source it came from: the delivered `{THUMB_IMAGE_ID}`
   * token becomes `{FILENAME}`, and any `{SWITCHGROUP}` is resolved so a
   * thumbnail URL cannot reach the network still templated — the playback
   * path gets that step in `getPlaybackUrl()`, which thumbnails never pass
   * through.
   */
  private static normalizeThumbnailTemplate(template: string): string {
    return PlaybackURLManager.applySwitchGroupResolution(
      template.replaceAll(
        PlaybackURLManager.THUMBNAIL_FRAME_PLACEHOLDER_DELIVERED,
        PlaybackURLManager.THUMBNAIL_FRAME_PLACEHOLDER
      )
    );
  }

  /**
   * Resolves the pre-session playback URL for VOD content.
   *
   * VOD is session-driven, like NDVR: the full title's URL is the session's
   * `playlistUrl`, which {@link selectPlaybackUrl} returns ahead of this, so
   * an empty result here is the normal case — playback starts when the
   * session responds. The watch-action kernel's `playUri` is honoured as a
   * fallback extension point when a provider supplies one.
   *
   * A session-less preview never reaches here: it is resolved, from the
   * kernel's `assetUrl`, before the fast-tune gate in
   * {@link selectPlaybackUrl}.
   */
  private getVodPlaybackUrl(_playable: IPlayable, metadata: IMetadata): string {
    const playUri = this.extractPlayUriFromKernel(metadata.watchVodAction);

    if (playUri !== "") {
      return playUri;
    }

    Log.d(
      PlaybackURLManager.TAG,
      "getVodPlaybackUrl() - no pre-session URL; awaiting the session"
    );
    return "";
  }

  /**
   * The preview asset URL off the VOD watch action, upgraded to HTTPS.
   *
   * The asset is a plain progressive or manifest URL the catalog may deliver
   * as `http://`, which an HTTPS-served client cannot load — Kaveri upgrades
   * it the same way (`VideoManager.getHttpsConvertedUrl`).
   */
  private static getVodPreviewAssetUrl(metadata: IMetadata): string {
    const watchVodAction = metadata.watchVodAction;
    const kernel =
      watchVodAction !== undefined && watchVodAction !== null
        ? watchVodAction.kernel
        : undefined;
    const assetUrl =
      kernel !== undefined && kernel !== null ? kernel.assetUrl : undefined;

    if (typeof assetUrl !== "string" || assetUrl === "") {
      Log.e(
        PlaybackURLManager.TAG,
        "getVodPreviewAssetUrl() - watch action carries no assetUrl"
      );
      return "";
    }

    return PlaybackURLManager.toHttps(assetUrl);
  }

  /** Rewrites a leading `http://` to `https://`; any other URL is unchanged. */
  private static toHttps(url: string): string {
    return url.replace(HTTP_SCHEME_PATTERN, "https://");
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Attempts to extract a `playUri` string from the `kernel` property
   * of a watch action record. Returns an empty string when unavailable.
   */
  private extractPlayUriFromKernel(
    watchAction: Record<string, unknown> | undefined
  ): string {
    if (watchAction === undefined || watchAction === null) {
      return "";
    }

    if (
      !("kernel" in watchAction) ||
      watchAction.kernel === undefined ||
      watchAction.kernel === null
    ) {
      return "";
    }

    const kernel = watchAction.kernel as Record<string, unknown>;

    if (
      "playUri" in kernel &&
      typeof kernel.playUri === "string" &&
      kernel.playUri !== ""
    ) {
      return kernel.playUri;
    }

    return "";
  }

  /**
   * Rewrites a CMAF playback URL to its DASH-packaged equivalent on clients
   * that cannot play CMAF: Firefox (desktop and iOS) and Samsung Tizen TVs
   * older than 6.5. All other clients get the URL unchanged.
   */
  private getModifiedUrl(url: string): string {
    if (
      DeviceModel.isFirefox() ||
      DeviceModel.isFirefoxIOS() ||
      PlaybackURLManager.isTizenBelowCmafSupport()
    ) {
      return url.replace("/mm/cmaf", "/mm/dash");
    }
    return url;
  }

  /**
   * True on a Samsung Tizen device whose OS version is below 6.5.
   *
   * A version that cannot be parsed (the platform reports "Unknown") is
   * treated as supported, so only devices we positively identify as legacy
   * are moved off the default CMAF URL.
   */
  private static isTizenBelowCmafSupport(): boolean {
    if (!DeviceModel.isTizenPlatform()) {
      return false;
    }

    const version = DeviceModel.getTVProperties().getPlatformVersion();
    const parts = version.split(".");
    const major = Number.parseInt(parts[0], 10);

    if (Number.isNaN(major)) {
      return false;
    }

    if (major !== PlaybackURLManager.TIZEN_MIN_CMAF_MAJOR) {
      return major < PlaybackURLManager.TIZEN_MIN_CMAF_MAJOR;
    }

    const minor = Number.parseInt(parts[1], 10);

    return (
      (Number.isNaN(minor) ? 0 : minor) <
      PlaybackURLManager.TIZEN_MIN_CMAF_MINOR
    );
  }
}
